import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api, ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LoadingLine, Skeleton, SkeletonCard } from '@/components/ui/States';
import { AttemptEnded } from '@/features/runner/AttemptEnded';
import { ResultsPage } from '@/features/results/ResultsPage';
import { normalizeAttemptResult } from '@/features/results/types';
import { useAsyncResultsRefresh } from '@/features/results/useAsyncResultsRefresh';

export const Route = createFileRoute('/attempts/$attemptId/results')({
  component: AttemptResultsPage,
});

function AttemptResultsPage() {
  const { attemptId } = Route.useParams();
  const navigate = useNavigate();

  const resultsQuery = useQuery({
    queryKey: ['attempt-results', attemptId],
    queryFn: () => api.get('/api/v1/attempts/{attemptId}/results', { params: { attemptId } }),
    retry: (count, error) => {
      if (error instanceof ApiError && error.status === 409) return false;
      return count < 1;
    },
  });

  useEffect(() => {
    if (!(resultsQuery.error instanceof ApiError) || resultsQuery.error.status !== 409) return;
    const body = resultsQuery.error.body;
    if (
      typeof body === 'object' &&
      body !== null &&
      'code' in body &&
      Reflect.get(body, 'code') === 'NOT_SCORED' &&
      Reflect.get(body, 'status') !== 'abandoned'
    ) {
      void navigate({ to: '/attempts/$attemptId', params: { attemptId } });
    }
  }, [attemptId, navigate, resultsQuery.error]);

  useAsyncResultsRefresh(attemptId, resultsQuery.data?.scoreRunStatus);

  if (resultsQuery.isPending) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-55 w-full rounded-card" />
        <SkeletonCard />
      </div>
    );
  }

  if (resultsQuery.isError) {
    if (resultsQuery.error instanceof ApiError && resultsQuery.error.status === 409) {
      const conflict = resultsQuery.error.body;
      if (
        typeof conflict === 'object' &&
        conflict !== null &&
        Reflect.get(conflict, 'status') === 'abandoned'
      ) {
        return <AttemptEnded />;
      }
      return <LoadingLine>Taking you back to your test.</LoadingLine>;
    }
    if (resultsQuery.error instanceof ApiError && resultsQuery.error.status === 403) {
      const body = resultsQuery.error.body;
      let code: unknown;
      if (body && typeof body === 'object') {
        code = Reflect.get(body, 'code');
        if (code === undefined) {
          const message = Reflect.get(body, 'message');
          if (message && typeof message === 'object') {
            code = Reflect.get(message, 'code');
          }
        }
      }
      if (code === 'PLAN_REQUIRED') {
        return (
          <Card padding="lg" className="mx-auto grid max-w-prose gap-4">
            <h1 className="font-display text-h2">You need an active plan</h1>
            <p className="text-body text-ink-2">
              Your results are saved. Buy a plan to start a new test.
            </p>
            <div>
              <Button onClick={() => void navigate({ to: '/pricing' })}>See plans</Button>
            </div>
          </Card>
        );
      }
    }
    return (
      <Alert tone="danger" title="We could not load your results.">
        Reload the page to try again. Nothing is lost.
      </Alert>
    );
  }

  return <ResultsPage result={normalizeAttemptResult(resultsQuery.data)} />;
}
